import {
  PortalDsAlert,
  PortalDsButton,
  PortalDsCard,
  PortalDsSectionHeader,
} from 'src/components/portal-design-system';
import { formatBytesProcessed } from '../helpers/data-transfers.helpers';
import type { SqlPreviewResult } from '../types/data-transfers.types';

interface SqlPreviewPanelProps {
  preview: SqlPreviewResult | null;
  previewError: string | null;
  isPreviewing: boolean;
  onPreview: () => void;
  previewDisabled?: boolean;
}

export function SqlPreviewPanel({
  preview,
  previewError,
  isPreviewing,
  onPreview,
  previewDisabled = false,
}: SqlPreviewPanelProps) {
  const columns = preview?.sample_rows?.length
    ? Object.keys(preview.sample_rows[0] ?? {})
    : [];

  return (
    <PortalDsCard className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <PortalDsSectionHeader
          title="Dry-run preview"
          description="Validates the SQL with a BigQuery dry-run (no destination writes). Sample rows are returned only for SELECT statements."
        />
        <PortalDsButton
          type="button"
          variant="secondary"
          disabled={previewDisabled || isPreviewing}
          onClick={onPreview}
        >
          {isPreviewing ? 'Previewing…' : 'Run preview'}
        </PortalDsButton>
      </div>

      {previewError ? (
        <PreviewQueryError
          title="Preview failed"
          message={previewError}
        />
      ) : null}

      {preview ? (
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-lg border border-[#EDEEF2] bg-[#F7F8FA] p-4">
              <p className="text-[11px] font-bold uppercase tracking-[0.5px] text-[#9CA3AF]">
                Bytes processed (estimate)
              </p>
              <p className="mt-1 text-lg font-semibold text-[#0D0F1A]">
                {formatBytesProcessed(preview.bytes_processed)}
              </p>
            </div>
            <div className="rounded-lg border border-[#EDEEF2] bg-[#F7F8FA] p-4">
              <p className="text-[11px] font-bold uppercase tracking-[0.5px] text-[#9CA3AF]">
                Sample rows
              </p>
              <p className="mt-1 text-lg font-semibold text-[#0D0F1A]">
                {preview.sample_rows.length}
              </p>
            </div>
          </div>

          {preview.sample_error ? (
            <PreviewQueryError message={preview.sample_error} />
          ) : null}

          {columns.length > 0 ? (
            <div className="overflow-auto rounded border border-gray-300">
              <table className="min-w-full text-left text-sm">
                <thead className="bg-gray-100 text-xs uppercase tracking-wide text-gray-600">
                  <tr>
                    {columns.map((column) => (
                      <th
                        key={column}
                        className="px-3 py-2 font-medium"
                      >
                        {column}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.sample_rows.map((row, rowIndex) => (
                    <tr
                      key={rowIndex}
                      className="border-t border-gray-200"
                    >
                      {columns.map((column) => (
                        <td
                          key={column}
                          className="px-3 py-2 font-mono text-xs text-gray-800"
                        >
                          {formatCellValue(row[column])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : !preview.sample_error ? (
            <p className="text-sm text-gray-500">
              No sample rows returned. DML statements (MERGE / CREATE / INSERT) are validated by dry-run only.
            </p>
          ) : null}
        </div>
      ) : previewError ? null : (
        <p className="text-sm text-gray-500">
          Preview has not been run yet. Use the button above to validate SQL without writing data.
        </p>
      )}
    </PortalDsCard>
  );
}

function PreviewQueryError({
  message,
  title = 'BigQuery error',
}: {
  message: string;
  title?: string;
}) {
  return (
    <PortalDsAlert
      variant="danger"
      title={title}
    >
      <p className="whitespace-pre-wrap break-words">
        {message}
      </p>
    </PortalDsAlert>
  );
}

function formatCellValue(value: unknown): string {
  if (value === null || value === undefined) {
    return '—';
  }
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  return String(value);
}
